import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { issueGithubUrl, issueLabel, type RepoIssueRow as Row } from '../lib/issues';
import { orgHref } from '../lib/org';
import { absolute, ago } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { IssueHandoffPill } from './IssueHandoffPill';
import { StatusPill } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// The repo's tracked issues as a dense data table (spec §3.15, arc 101 slice 06).
// Presenter — the tab owns the accumulated rows, the filters, and paging.
//
// Each row is triage state plus the cached GitHub title (#980; `''` on
// pre-cache rows → the bare `#<n>`). The number links OUT to GitHub for the full
// prose and the last session links IN to the timeline that decided it. Server
// enum values are label-mapped defensively: an unknown value renders verbatim
// rather than a loud missing-key marker. "Details" opens the drawer with the
// forced levers.

export function RepoIssuesTable(props: {
  rows: readonly Row[];
  encoded: string;
  onOpen: (row: Row) => void;
}): JSX.Element {
  const placeholder = (): JSX.Element => <span class="repo-issues__none">{UNSET}</span>;

  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: 'issue',
      rowHeader: true,
      header: t('repo_detail.issues_col_issue'),
      mono: true,
      cell: (row) => (
        <>
          {/* The full issue text lives on GitHub — the number is the way out. */}
          <a
            class="repo-issues__gh"
            href={issueGithubUrl(props.encoded, row.github_number)}
            target="_blank"
            rel="noreferrer"
          >
            {`#${row.github_number}`}
          </a>
          {/* `''` = cached before the title backfill → the number stands alone.
              The separator rides on the title branch so a pre-cache row stays bare. */}
          <Show when={row.title}>
            {(title) => (
              <>
                {' '}
                <span class="repo-issues__title" title={title()}>
                  {title()}
                </span>
              </>
            )}
          </Show>
        </>
      ),
    },
    {
      key: 'state',
      header: t('repo_detail.issues_col_state'),
      cell: (row) => (
        <StatusPill tone={row.state === 'open' ? 'success' : 'neutral'}>
          {issueLabel('issues_state', row.state)}
        </StatusPill>
      ),
    },
    {
      key: 'qualification',
      header: t('repo_detail.issues_col_qualification'),
      cell: (row) => issueLabel('issues_qual', row.qualification),
    },
    {
      key: 'handoff',
      header: t('repo_detail.issues_col_handoff'),
      cell: (row) => <IssueHandoffPill status={row.handoff_status} />,
    },
    {
      key: 'last_action',
      header: t('repo_detail.issues_col_last_action'),
      cell: (row) => (
        <Show when={row.last_agent_action_at} fallback={placeholder()}>
          {(when) => (
            <time datetime={when()} title={absolute(when())}>
              {ago(when())}
            </time>
          )}
        </Show>
      ),
    },
    {
      key: 'actions',
      header: t('repo_detail.issues_col_actions'),
      srOnlyHeader: true,
      align: 'end',
      cell: (row) => (
        <div class="data-table__actions">
          <Button
            variant="outline"
            size="sm"
            data-action="details"
            onClick={() => props.onOpen(row)}
          >
            {t('repo_detail.issues_open_detail')}
          </Button>
          {/* Only rendered once a loop has touched the issue — there is no
              timeline to open before the first session. */}
          <Show when={row.last_session_id}>
            {(sessionId) => (
              <A
                class={buttonClass({ variant: 'ghost', size: 'sm' })}
                href={orgHref(`/audit/${sessionId()}`)}
              >
                {t('repo_detail.issues_view_session')}
              </A>
            )}
          </Show>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      label={t('repo_detail.issues_table_label')}
      class="repo-issues"
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
